import { useIsFocused, useRoute } from '@react-navigation/native';
import { useGlobalConfig } from 'context/GlobalContext';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { AppState, View, useWindowDimensions } from 'react-native';
import { useGetProgramQuery } from 'services/cms';
import { ProgramType } from 'utils/enum';
import { Platform } from 'utils/platform';
import { modifyVastUrl } from 'utils/vast';

import { DynamicListItemProps } from 'components/Dynamic/ListItem';
import Player from 'components/Player';
import SpallaPlayerWeb from 'components/Player/SpallaPlayer';
import { usePlayer } from 'components/Player/context';
import ProgramContext from 'components/Program/context';

type OrientationType =
  | 'portrait-primary'
  | 'portrait-secondary'
  | 'landscape-primary'
  | 'landscape-secondary';

type LivePlayerProps = DynamicListItemProps &
  Partial<{
    searchPrograms: CmsApiGenericParams<CmsProgram>;
    _name: string;
  }>;

const LivePlayerCommon: FC<LivePlayerProps> = () => {
  const route = useRoute();
  const { setNextTrack, pause, play, isFullScreen } = usePlayer();
  const { ads, homeLivePlayer, spalla_player_config } = useGlobalConfig();
  const isFocused = useIsFocused();
  const { height, width } = useWindowDimensions();
  const [mediaId, setMediaId] = useState<string>();
  const wasPausedByScrollRef = useRef(false);
  // const previousScrollingStateRef = useRef(false);
  const [orientation, setOrientation] = useState<OrientationType>('portrait-primary');

  const width60Percent = width * 0.45;
  console.log('width60Percent', width60Percent);
  const adjustedPlayerHeight = orientation.includes('landscape') ? height - 120 : 'auto';
  const adjustedPlayerHeightToSpalla = orientation.includes('landscape') ? width60Percent : '100%';

  const hasHomeLiveProgramID = homeLivePlayer?.homeLiveProgramID
    ? homeLivePlayer.homeLiveProgramID
    : 'band-ao-vivo';

  const { data: program, error: programError } = useGetProgramQuery(hasHomeLiveProgramID);

  useEffect(() => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      const subscription = AppState.addEventListener('change', (nextAppState) => {
        if (nextAppState === 'background' || nextAppState === 'inactive') {
          pause();
        }
      });

      return () => subscription?.remove();
    }
  }, []);

  const shouldReturnNull = programError || !program;

  useEffect(() => {
    const handleOrientationChange = () => {
      if (typeof window !== 'undefined' && window.screen?.orientation) {
        const currentOrientation = window.screen.orientation.type as OrientationType;
        setOrientation(currentOrientation);
      }
    };

    if (Platform.OS === 'web') {
      window.addEventListener('orientationchange', handleOrientationChange);
      window.addEventListener('resize', handleOrientationChange);

      return () => {
        window.removeEventListener('orientationchange', handleOrientationChange);
        window.removeEventListener('resize', handleOrientationChange);
      };
    }
  }, []);

  // useEffect(() => {
  //   if (Platform.OS === 'web') return;

  //   const currentScrollingDown = isScrollingDown === true;
  //   const scrollStateChanged = previousScrollingStateRef.current !== currentScrollingDown;

  //   if (scrollStateChanged) {
  //     if (currentScrollingDown && isPlaying) {
  //       wasPausedByScrollRef.current = true;
  //       pause();
  //     } else if (!currentScrollingDown && wasPausedByScrollRef.current) {
  //       wasPausedByScrollRef.current = false;
  //       play();
  //     }

  //     previousScrollingStateRef.current = currentScrollingDown;
  //   }
  // }, [isScrollingDown, isPlaying, pause, play]);

  useEffect(() => {
    setNextTrack(undefined);

    return () => {
      setNextTrack(undefined);
      // if (wasPausedByScrollRef.current) {
      //   wasPausedByScrollRef.current = false;
      //   play();
      // }
    };
  }, [setNextTrack]);

  useEffect(() => {
    const shouldSetMediaId = program && program.mediaId && program.ads?.disable_autoPlay !== true;

    if (shouldSetMediaId) {
      setMediaId(program.mediaId);
    }
  }, [program]);

  const playerProps = useMemo(() => {
    if (!mediaId || !program) return undefined;

    const hasRequiredProgramData = program.title && program._id && program.url;
    if (!hasRequiredProgramData) {
      return undefined;
    }

    const videoTracks = [{ language: 'origin', trackId: mediaId }];

    const media: PlayerMedia = {
      title: program.title || '',
      image: [program.imageVertical?.url ?? '', program.imageHorizontal?.url ?? ''],
      programId: program._id,
      programTitle: program.title || '',
      programUrl: program.url || '',
      ratingValue: program.rating || '',
      ratingDescription: program.rateDescription || '',
      videoTracks: videoTracks,
    };

    const isLive = program.type === ProgramType.LIVE || program.type === ProgramType.RADIO;
    const disableAds = ads?.disableAds || program.ads?.disableAds;
    const adTagUrl = disableAds
      ? undefined
      : ads?.vast
      ? modifyVastUrl(ads.vast, program)
      : undefined;

    return {
      media,
      isLive,
      adTagUrl,
      adsExpirationTime: ads?.adsExpirationTime,
      autoplay: true,
    };
  }, [program, mediaId, route, ads]);

  if (shouldReturnNull || !playerProps || !isFocused) {
    return null;
  }

  if (Platform.OS === 'web') {
    return (
      <ProgramContext.Provider value={program}>
        {program?.spallaID && spalla_player_config?.enable_spalla_player_web ? (
          <View
            style={{
              backgroundColor: 'black',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              height: 'auto',
            }}>
            <View
              style={{
                width: Platform.isMobileWeb() ? adjustedPlayerHeightToSpalla : '50%',
              }}>
              <SpallaPlayerWeb streamId={program.spallaID} autoplay isLive program={program} />
            </View>
          </View>
        ) : (
          <View
            style={{
              width: '100%',
              height: Platform.isMobileWeb() ? adjustedPlayerHeight : 500,
              aspectRatio: 16 / 9,
              // borderRadius: 8,
              // overflow: 'hidden',
            }}>
            <Player {...playerProps} autoplay />
          </View>
        )}
      </ProgramContext.Provider>
    );
  }

  return (
    <ProgramContext.Provider value={program}>
      <View
        style={{
          width: '100%',
          height: isFullScreen ? height : 'auto',
        }}>
        <Player
          {...playerProps}
          enablePip={false}
          enableInternalAndroidAutoRotate={false}
          playerLocation="home"
        />
      </View>
    </ProgramContext.Provider>
  );
};

export default LivePlayerCommon;
